/**
 * Product Block Layout Engine
 *
 * Handles the three-layer enhanced template system:
 *   1. static_shapes  – backgrounds, decorations (rendered as-is)
 *   2. shared_shapes  – price display shown once (bound to products[0])
 *   3. product_block  – repeats per product with grid/horizontal/vertical layout
 *
 * Backward compatible: if a cell has no enhanced fields it returns null so the
 * caller can fall back to the legacy flat-shapes rendering path.
 */

/**
 * Detect whether a cell uses the enhanced template system.
 */
export function isEnhancedCell(cell) {
  return !!(cell && (cell.static_shapes || cell.shared_shapes || cell.product_block));
}

/**
 * Build the final shapes array for an enhanced cell given its products.
 *
 * @param {Object} cell  – the grid cell object
 * @param {Array}  products – array of product data objects assigned to this cell
 * @returns {Array} flat array of shapes ready for rendering
 */
export function buildEnhancedShapes(cell, products = []) {
  const shapes = [];
  let nextId = 9000; // high start to avoid collisions with existing shape ids

  // Helper to parse JSON strings (data may come from DB as strings)
  const ensureArray = (data) => {
    if (!data) return null;
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return null; }
    }
    return null;
  };

  // 1. Static shapes (backgrounds, decorations)
  const staticShapes = ensureArray(cell.static_shapes);
  if (staticShapes) {
    staticShapes.forEach(shape => {
      shapes.push({ ...shape });
    });
  }

  // 2. Shared shapes (price display, bound to products[0])
  const sharedShapes = ensureArray(cell.shared_shapes);
  if (sharedShapes) {
    const primaryProduct = products[0] || {};
    sharedShapes.forEach(shape => {
      const s = { ...shape };
      if (s.linkedProductProperty && primaryProduct) {
        s.text = resolveProductProperty(s.linkedProductProperty, primaryProduct);
      }
      shapes.push(s);
    });
  }

  // 3. Product block – repeat per product
  if (cell.product_block && cell.product_block.shapes && products.length > 0) {
    const pb = cell.product_block;
    const blockShapes = ensureArray(pb.shapes);
    if (!blockShapes) return shapes;
    const blockW = pb.block_width || 300;
    const blockH = pb.block_height || 300;
    const gap = pb.gap || 8;
    const maxProducts = Math.min(products.length, pb.max_products || 4);
    const layouts = pb.layouts || {};

    // Calculate cell content area (use templateWidth/Height as reference)
    const cellW = cell.templateWidth || cell.dimensions?.width || 400;
    const cellH = cell.templateHeight || cell.dimensions?.height || 400;

    // Resolve the pattern for this product count
    const pattern = layouts[maxProducts] || getDefaultPattern(maxProducts);

    for (let i = 0; i < maxProducts; i++) {
      const product = products[i];
      const { offsetX, offsetY, scaleX, scaleY } = getBlockPosition(
        i, maxProducts, pattern, blockW, blockH, cellW, cellH, gap
      );

      blockShapes.forEach(shape => {
        const s = { ...shape, id: `pb-${nextId++}` };

        // Scale the shape to fit the allocated block size
        if (s.x !== undefined) s.x = s.x * scaleX + offsetX;
        if (s.y !== undefined) s.y = s.y * scaleY + offsetY;
        if (s.width) s.width *= scaleX;
        if (s.height) s.height *= scaleY;
        if (s.radius) s.radius *= scaleX;
        if (s.fontSize) s.fontSize *= scaleX;
        if (s.cornerRadius) s.cornerRadius *= scaleX;
        if (s.innerRadius) s.innerRadius *= scaleX;
        if (s.outerRadius) s.outerRadius *= scaleX;
        if (s.strokeWidth) s.strokeWidth *= Math.min(scaleX, scaleY);
        if (s.points) s.points = s.points.map(p => p * scaleX);

        // Substitute product data
        if (s.linkedProductProperty && product) {
          s.text = resolveProductProperty(s.linkedProductProperty, product);
        }
        if (s.type === 'image' && s.linkedProductProperty && product) {
          const imgSrc = resolveProductImage(s.linkedProductProperty, product);
          if (imgSrc) {
            s.imageUrl = imgSrc;
            s.pendingImageUrl = imgSrc;
          }
        }

        // Set linkedProductNumber to the product index for identification
        s.linkedProductNumber = (i + 1).toString();

        shapes.push(s);
      });
    }
  }

  return shapes;
}

/**
 * Return bounding boxes for each product slot so the UI can overlay
 * clickable hit areas on top of the Konva canvas.
 *
 * @param {Object} cell – the grid cell object (needs product_block, products, templateWidth/Height)
 * @param {number} renderWidth – actual rendered pixel width of the cell
 * @param {number} renderHeight – actual rendered pixel height of the cell
 * @returns {Array<{x,y,w,h,productIndex}>} slot bounds in rendered pixels
 */
export function getProductSlotBounds(cell, renderWidth, renderHeight) {
  if (!cell?.product_block || !cell.products?.length) return [];
  const pb = cell.product_block;
  const cellW = cell.templateWidth || cell.dimensions?.width || 400;
  const cellH = cell.templateHeight || cell.dimensions?.height || 400;
  const gap = pb.gap || 8;
  const maxProducts = Math.min(cell.products.length, pb.max_products || 4);
  const layouts = pb.layouts || {};
  const pattern = layouts[maxProducts] || getDefaultPattern(maxProducts);
  const slots = getPatternSlots(pattern, cellW, cellH, gap);
  const scaleX = renderWidth / cellW;
  const scaleY = renderHeight / cellH;

  return slots.slice(0, maxProducts).map((slot, i) => ({
    x: slot.x * scaleX,
    y: slot.y * scaleY,
    w: slot.w * scaleX,
    h: slot.h * scaleY,
    productIndex: i,
  }));
}

/**
 * Return the default layout pattern for a given product count.
 */
function getDefaultPattern(count) {
  switch (count) {
    case 1: return 'full';
    case 2: return 'side-by-side';
    case 3: return 'row';
    case 4: return 'grid-2x2';
    default: return 'grid-2x2';
  }
}

/**
 * Named layout patterns.
 * Each returns an array of { x, y, w, h } slots as fractions of (cellW, cellH).
 * The gap is subtracted from the available space before computing slots.
 */
function getPatternSlots(pattern, cellW, cellH, gap) {
  switch (pattern) {
    // --- 1 product ---
    case 'full':
      return [{ x: 0, y: 0, w: cellW, h: cellH }];
    case 'centered': {
      const pw = cellW * 0.6;
      const ph = cellH * 0.6;
      return [{ x: (cellW - pw) / 2, y: (cellH - ph) / 2, w: pw, h: ph }];
    }

    // --- 2 products ---
    case 'side-by-side': {
      const sw = (cellW - gap) / 2;
      return [
        { x: 0, y: 0, w: sw, h: cellH },
        { x: sw + gap, y: 0, w: sw, h: cellH },
      ];
    }
    case 'stacked': {
      const sh = (cellH - gap) / 2;
      return [
        { x: 0, y: 0, w: cellW, h: sh },
        { x: 0, y: sh + gap, w: cellW, h: sh },
      ];
    }
    case 'big-left': {
      const bigW = (cellW - gap) * 0.6;
      const smallW = cellW - gap - bigW;
      const smallH = cellH * 0.55;
      return [
        { x: 0, y: 0, w: bigW, h: cellH },
        { x: bigW + gap, y: (cellH - smallH) / 2, w: smallW, h: smallH },
      ];
    }
    case 'overlap-right': {
      const ow = cellW * 0.58;
      return [
        { x: 0, y: 0, w: ow, h: cellH },
        { x: cellW - ow, y: 0, w: ow, h: cellH },
      ];
    }
    case 'overlap-stagger': {
      const osw = cellW * 0.58;
      const osh = cellH * 0.85;
      return [
        { x: 0, y: cellH - osh, w: osw, h: osh },
        { x: cellW - osw, y: 0, w: osw, h: osh },
      ];
    }
    case 'front-back': {
      // Back product: smaller, offset to top-right
      // Front product: larger, offset to bottom-left (rendered second = on top)
      const fbW = cellW * 0.62;
      const fbH = cellH * 0.78;
      return [
        { x: cellW - fbW, y: 0, w: fbW, h: fbH },
        { x: 0, y: cellH - fbH, w: fbW, h: fbH },
      ];
    }

    // --- 3 products ---
    case 'row': {
      const rw = (cellW - 2 * gap) / 3;
      return [
        { x: 0, y: 0, w: rw, h: cellH },
        { x: rw + gap, y: 0, w: rw, h: cellH },
        { x: 2 * (rw + gap), y: 0, w: rw, h: cellH },
      ];
    }
    case 'cluster': {
      const cw = cellW * 0.5;
      const ch = cellH * 0.75;
      return [
        { x: 0, y: cellH - ch, w: cw, h: ch },
        { x: (cellW - cw) / 2, y: 0, w: cw, h: ch },
        { x: cellW - cw, y: cellH - ch, w: cw, h: ch },
      ];
    }
    case 'pyramid': {
      // 1 centered on top, 2 side-by-side on bottom
      const pyW = cellW * 0.45;
      const pyH = (cellH - gap) / 2;
      return [
        { x: (cellW - pyW) / 2, y: 0, w: pyW, h: pyH },
        { x: 0, y: pyH + gap, w: pyW, h: pyH },
        { x: cellW - pyW, y: pyH + gap, w: pyW, h: pyH },
      ];
    }
    case 'fan': {
      // 3 products fanned/cascading with overlap, center one slightly higher
      const fanW = cellW * 0.48;
      const fanH = cellH * 0.82;
      const fanSpacing = (cellW - fanW) / 2;
      return [
        { x: 0, y: cellH - fanH, w: fanW, h: fanH },
        { x: fanSpacing, y: 0, w: fanW, h: fanH },
        { x: cellW - fanW, y: cellH - fanH, w: fanW, h: fanH },
      ];
    }
    case '1-big-2-small': {
      const bigW3 = (cellW - gap) * 0.6;
      const smallW3 = cellW - gap - bigW3;
      const smallH3 = (cellH - gap) / 2;
      return [
        { x: 0, y: 0, w: bigW3, h: cellH },
        { x: bigW3 + gap, y: 0, w: smallW3, h: smallH3 },
        { x: bigW3 + gap, y: smallH3 + gap, w: smallW3, h: smallH3 },
      ];
    }
    case 'top-row': {
      const topH = (cellH - gap) * 0.5;
      const botH = cellH - gap - topH;
      const botW = (cellW - gap) / 2;
      return [
        { x: 0, y: 0, w: cellW, h: topH },
        { x: 0, y: topH + gap, w: botW, h: botH },
        { x: botW + gap, y: topH + gap, w: botW, h: botH },
      ];
    }

    // --- 4 products ---
    case 'grid-2x2': {
      const gw = (cellW - gap) / 2;
      const gh = (cellH - gap) / 2;
      return [
        { x: 0, y: 0, w: gw, h: gh },
        { x: gw + gap, y: 0, w: gw, h: gh },
        { x: 0, y: gh + gap, w: gw, h: gh },
        { x: gw + gap, y: gh + gap, w: gw, h: gh },
      ];
    }
    case 'fan-4': {
      // 4 products fanned with overlap, outer ones slightly lower than inner
      const f4W = cellW * 0.38;
      const f4H = cellH * 0.75;
      const f4Step = (cellW - f4W) / 3;
      return [
        { x: 0, y: cellH - f4H, w: f4W, h: f4H },
        { x: f4Step, y: 0, w: f4W, h: f4H },
        { x: 2 * f4Step, y: 0, w: f4W, h: f4H },
        { x: cellW - f4W, y: cellH - f4H, w: f4W, h: f4H },
      ];
    }
    case '1-big-3-small': {
      const bigW4 = (cellW - gap) * 0.6;
      const smallW4 = cellW - gap - bigW4;
      const smallH4 = (cellH - 2 * gap) / 3;
      return [
        { x: 0, y: 0, w: bigW4, h: cellH },
        { x: bigW4 + gap, y: 0, w: smallW4, h: smallH4 },
        { x: bigW4 + gap, y: smallH4 + gap, w: smallW4, h: smallH4 },
        { x: bigW4 + gap, y: 2 * (smallH4 + gap), w: smallW4, h: smallH4 },
      ];
    }
    case 'row-4': {
      const r4w = (cellW - 3 * gap) / 4;
      return [
        { x: 0, y: 0, w: r4w, h: cellH },
        { x: r4w + gap, y: 0, w: r4w, h: cellH },
        { x: 2 * (r4w + gap), y: 0, w: r4w, h: cellH },
        { x: 3 * (r4w + gap), y: 0, w: r4w, h: cellH },
      ];
    }

    // Fallback: simple grid
    default: {
      const cols = Math.ceil(Math.sqrt(4));
      const rows = Math.ceil(4 / cols);
      const sw2 = (cellW - (cols - 1) * gap) / cols;
      const sh2 = (cellH - (rows - 1) * gap) / rows;
      const slots = [];
      for (let idx = 0; idx < 4; idx++) {
        const col = idx % cols;
        const row2 = Math.floor(idx / cols);
        slots.push({ x: col * (sw2 + gap), y: row2 * (sh2 + gap), w: sw2, h: sh2 });
      }
      return slots;
    }
  }
}

/**
 * Calculate position and scale for a product block at index i.
 */
function getBlockPosition(i, total, pattern, blockW, blockH, cellW, cellH, gap) {
  const slots = getPatternSlots(pattern, cellW, cellH, gap);
  const slot = slots[i] || slots[0];

  return {
    offsetX: slot.x,
    offsetY: slot.y,
    scaleX: slot.w / blockW,
    scaleY: slot.h / blockH,
  };
}

/**
 * Resolve a product property name to its value.
 */
function resolveProductProperty(prop, product) {
  switch (prop) {
    case 'product_name':
      return product.name || product.product_name || '';
    case 'product_description':
      return product.productDescription || product.product_description || '';
    case 'promo_price_dollars':
      return product.promoPrice
        ? `${Math.floor(product.promoPrice)}`
        : `${Math.floor(product.originalPrice || 0)}`;
    case 'promo_price_cents': {
      const price = product.promoPrice || product.originalPrice || 0;
      const num = Number(price);
      if (!isNaN(num)) {
        const [, cents] = num.toFixed(2).split('.');
        return cents;
      }
      return '00';
    }
    case 'original_price':
      return (product.originalPrice || product.original_price || '').toString();
    case 'price_unit':
      return product.priceUnit ? 'per ' + product.priceUnit : '';
    case 'savings':
      return ((product.originalPrice || 0) - (product.promoPrice || 0)).toFixed(2);
    case 'limited_qty':
      return product.limit_qty ? 'LIMIT OF ' + product.limit_qty : '';
    case 'promo_type':
      return product.promoType === 'BOGO' ? 'Buy 1 Get 1 Free' : (product.promoType || '');
    default:
      return '';
  }
}

/**
 * Resolve a product image property to URL.
 */
function resolveProductImage(prop, product) {
  if (prop === 'cleaned_img_large_src') {
    return product.cleanedImgLargeSrc || product.originalImgLargeSrc || null;
  }
  if (prop === 'original_img_large_src') {
    return product.originalImgLargeSrc || null;
  }
  return null;
}
